import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { Badge, Box, Callout, Collapse, Column, Divider, FullRow, Row, Switch, TextButton, Typography } from 'seagull';
import { Bulb } from 'seagull/icons';
import { VariableInlineRenderer } from '~/components/AgreementStudioPage/TemplateVariables/VariableInlineRenderer/VariableInlineRenderer';
import { useLocaleKeys } from '~/i18n';
import { Intent } from '~/infra/access-control';
import styles from './ServiceItem.module.scss';
export const DesktopServiceItem = (param)=>{
    let { service, isOptional, isSelected, onToggle, discountBadgeData, getBillingBadgeText, futureBillingText, useInlineFutureBilling, renderPrice, hasIncludedServices, hasExpandableContent, getServiceDescription, ServiceItemTestIds, resolvedValues } = param;
    var _service_bundle;
    const [isExpanded, setIsExpanded] = useState(false);
    const { t: reviewTexts } = useLocaleKeys('clientFlow.reviewServices');
    const toggleExpand = ()=>{
        if (hasExpandableContent) {
            setIsExpanded((prev)=>!prev);
        }
    };
    const stopPropagation = (e)=>e.stopPropagation();
    return /*#__PURE__*/ _jsx(Column, {
        className: styles.serviceItem,
        testId: ServiceItemTestIds.Root(),
        children: /*#__PURE__*/ _jsxs(Column, {
            className: styles.card,
            children: [
                /*#__PURE__*/ _jsxs(Column, {
                    gap: 8,
                    children: [
                        /*#__PURE__*/ _jsxs(FullRow, {
                            gap: 16,
                            alignItems: "center",
                            onClick: toggleExpand,
                            className: hasExpandableContent ? styles.clickableRow : undefined,
                            children: [
                                isOptional && /*#__PURE__*/ _jsx(Box, {
                                    onClick: stopPropagation,
                                    children: /*#__PURE__*/ _jsx(Switch, {
                                        bi: {
                                            name: 'select_optional_service'
                                        },
                                        intent: Intent.SelectOptionalService,
                                        checked: isSelected,
                                        testId: ServiceItemTestIds.OptionalToggle(),
                                        onChange: onToggle
                                    })
                                }),
                                /*#__PURE__*/ _jsx(Typography, {
                                    className: styles.serviceName,
                                    variant: "paragraph2",
                                    weight: "normal",
                                    skin: "standard",
                                    ellipsis: true,
                                    testId: ServiceItemTestIds.Name(),
                                    children: /*#__PURE__*/ _jsx(VariableInlineRenderer, {
                                        text: service.name,
                                        resolvedValues: resolvedValues
                                    })
                                }),
                                /*#__PURE__*/ _jsxs(Row, {
                                    gap: 8,
                                    children: [
                                        discountBadgeData && /*#__PURE__*/ _jsx(Badge, {
                                            rounded: true,
                                            outlined: true,
                                            skin: "primary",
                                            className: styles.billingBadge,
                                            testId: ServiceItemTestIds.DiscountBadge(),
                                            children: discountBadgeData.text
                                        }),
                                        /*#__PURE__*/ _jsx(Badge, {
                                            rounded: true,
                                            outlined: true,
                                            skin: "primary",
                                            className: styles.billingBadge,
                                            testId: ServiceItemTestIds.BillingBadge(),
                                            children: getBillingBadgeText()
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsx(Box, {
                                    testId: ServiceItemTestIds.Price(),
                                    children: renderPrice()
                                }),
                                hasExpandableContent && /*#__PURE__*/ _jsxs(Row, {
                                    gap: 16,
                                    alignItems: "center",
                                    onClick: stopPropagation,
                                    children: [
                                        /*#__PURE__*/ _jsx(Divider, {
                                            orientation: "vertical",
                                            margin: 0,
                                            className: styles.viewMoreDivider
                                        }),
                                        /*#__PURE__*/ _jsx(TextButton, {
                                            underline: "none",
                                            weight: "normal",
                                            variant: "paragraph2",
                                            className: styles.viewMoreButton,
                                            onClick: toggleExpand,
                                            testId: ServiceItemTestIds.ExpandButton(),
                                            children: isExpanded ? reviewTexts.viewLess() : reviewTexts.viewMore()
                                        })
                                    ]
                                })
                            ]
                        }),
                        hasIncludedServices && /*#__PURE__*/ _jsxs(Column, {
                            gap: 4,
                            testId: ServiceItemTestIds.IncludedServices(),
                            children: [
                                /*#__PURE__*/ _jsx(Typography, {
                                    variant: "paragraph2",
                                    weight: "normal",
                                    children: reviewTexts.includedServicesColon()
                                }),
                                /*#__PURE__*/ _jsx("ul", {
                                    className: styles.includedServicesList,
                                    children: (_service_bundle = service.bundle) === null || _service_bundle === void 0 ? void 0 : _service_bundle.services.map((includedService)=>/*#__PURE__*/ _jsx("li", {
                                            children: /*#__PURE__*/ _jsx(Typography, {
                                                variant: "paragraph2",
                                                skin: "secondary",
                                                weight: "light",
                                                children: /*#__PURE__*/ _jsx(VariableInlineRenderer, {
                                                    text: includedService.name,
                                                    resolvedValues: resolvedValues
                                                })
                                            })
                                        }, includedService.serviceTemplateId))
                                })
                            ]
                        })
                    ]
                }),
                hasExpandableContent && /*#__PURE__*/ _jsx(Collapse, {
                    in: isExpanded,
                    children: /*#__PURE__*/ _jsxs(Column, {
                        gap: 16,
                        className: styles.expandedContent,
                        testId: ServiceItemTestIds.ExpandedContent(),
                        children: [
                            getServiceDescription({
                                hideIncludedServices: true
                            }),
                            futureBillingText && (useInlineFutureBilling ? /*#__PURE__*/ _jsxs(Row, {
                                gap: 8,
                                alignItems: "center",
                                className: styles.futureBillingInline,
                                testId: ServiceItemTestIds.FutureBillingCallout(),
                                children: [
                                    /*#__PURE__*/ _jsx(Box, {
                                        className: styles.futureBillingIcon,
                                        children: /*#__PURE__*/ _jsx(Bulb, {})
                                    }),
                                    /*#__PURE__*/ _jsx(Typography, {
                                        variant: "paragraph2",
                                        weight: "light",
                                        children: futureBillingText
                                    })
                                ]
                            }) : /*#__PURE__*/ _jsx(Callout, {
                                variant: "paragraph2",
                                intent: "info",
                                icon: /*#__PURE__*/ _jsx(Bulb, {}),
                                classes: {
                                    wrapper: styles.futureBillingCalloutWrapper,
                                    main: styles.futureBillingCallout
                                },
                                testId: ServiceItemTestIds.FutureBillingCallout(),
                                children: futureBillingText
                            }))
                        ]
                    })
                })
            ]
        })
    });
};
